(function () {
  const GRID_SELECTOR = '[data-rq-availability-patch]';
  const PRODUCT_GRID_SELECTOR = '[data-render-grid]';
  const CARD_SELECTOR = '[data-rq-product-card]';
  const SKELETON_SELECTOR = '.rq-collection-patch-skeleton';
  const BYPASS_PARAM = 'rq_show_all';
  const DEBUG_PARAM = 'rq_card_debug';
  const PRE_SKELETON_CLASS = 'rq-collection-pre-skeleton';
  const PAGE_CACHE_PREFIX = 'rqCollectionAvailabilityPage:';
  const PAGE_CACHE_INDEX_KEY = 'rqCollectionAvailabilityPage:index';
  const PAGE_CACHE_VERSION = '5';
  const MAX_STORED_PAGE_SLICES = 12;
  const PREFETCH_PAGES_AHEAD = 2;
  const STREAM_ROW_DELAY = 80;
  const realOutOfStockCardsCache = new Map();
  const collectionCardsCache = new Map();
  const targetSliceCache = new Map();
  const prefetchedPages = new Set();

  const toNumber = (value, fallback = 0) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  };

  const getProductGrid = (grid) => {
    return grid.querySelector(PRODUCT_GRID_SELECTOR) || grid.querySelector('.grid');
  };

  const getLoader = (productGrid) => {
    return productGrid.querySelector('.filters-grid_loader');
  };

  const getGridControls = (productGrid) => {
    return productGrid.querySelector('.pagination-wrapper') || getLoader(productGrid);
  };

  const getCards = (productGrid) => {
    return Array.from(productGrid.querySelectorAll(CARD_SELECTOR));
  };

  const getRowSize = (productGrid) => {
    const computedStyle = window.getComputedStyle(productGrid);
    const gridTemplateColumns = computedStyle.gridTemplateColumns;

    if (gridTemplateColumns && gridTemplateColumns !== 'none') {
      const columns = gridTemplateColumns.split(' ').filter(Boolean).length;
      if (columns > 0) return columns;
    }

    return Math.max(
      1,
      Math.round(toNumber(computedStyle.getPropertyValue('--desktop-columns-count'), 1))
    );
  };

  const getCurrentPage = () => {
    const page = toNumber(new URL(window.location.href).searchParams.get('page'), 1);
    return page > 0 ? page : 1;
  };

  const getTotalPages = (grid) => {
    const pageSize = toNumber(grid.dataset.rqPageSize);
    const visiblePages = getPaginationPageNumbers(grid);
    const totalCount = toNumber(grid.dataset.rqInStockCount) + toNumber(grid.dataset.rqOutOfStockCount);

    if (grid.dataset.rqBundleOnly === 'true' && visiblePages.length > 0) {
      return Math.max(...visiblePages);
    }

    return pageSize > 0 ? Math.ceil(totalCount / pageSize) : 1;
  };

  const getPaginationPageNumbers = (grid) => {
    return Array.from(grid.querySelectorAll('.pagination-wrapper a, .pagination-wrapper [aria-current], .pagination-wrapper .pagination__item'))
      .map((element) => {
        return toNumber(element.textContent.trim(), 0);
      })
      .filter((page) => page > 0);
  };

  const buildNormalizedSearch = (page) => {
    const url = new URL(window.location.href);

    url.searchParams.delete('section_id');
    url.searchParams.delete(BYPASS_PARAM);
    url.searchParams.delete(DEBUG_PARAM);

    if (page > 1) {
      url.searchParams.set('page', String(page));
    } else {
      url.searchParams.delete('page');
    }

    url.searchParams.sort();

    return url.searchParams.toString();
  };

  const buildContextCacheKey = (grid) => {
    return [
      PAGE_CACHE_VERSION,
      window.location.pathname,
      buildNormalizedSearch(1),
      grid.dataset.id,
      grid.dataset.rqBundleOnly,
      grid.dataset.rqPageSize,
      grid.dataset.rqInStockParam,
      grid.dataset.rqInStockValue,
      grid.dataset.rqInStockCount,
      grid.dataset.rqOutOfStockParam,
      grid.dataset.rqOutOfStockValue,
      grid.dataset.rqOutOfStockCount
    ].join('|');
  };

  const buildPageCacheKey = (grid, page) => {
    return [
      buildContextCacheKey(grid),
      page
    ].join('|');
  };

  const parseCachedCards = (html) => {
    const template = document.createElement('template');
    template.innerHTML = html;

    return Array.from(template.content.querySelectorAll(CARD_SELECTOR));
  };

  const getStoredPageCards = (cacheKey) => {
    try {
      const html = sessionStorage.getItem(`${PAGE_CACHE_PREFIX}${cacheKey}`);

      return html ? parseCachedCards(html) : null;
    } catch (error) {
      return null;
    }
  };

  const removeStoredPageCards = (cacheKey) => {
    try {
      sessionStorage.removeItem(`${PAGE_CACHE_PREFIX}${cacheKey}`);
      const index = JSON.parse(sessionStorage.getItem(PAGE_CACHE_INDEX_KEY) || '[]')
        .filter((key) => key !== cacheKey);
      sessionStorage.setItem(PAGE_CACHE_INDEX_KEY, JSON.stringify(index));
    } catch (error) {
      // Cache invalidation is best-effort; a failed removal should fall back to fetching.
    }
  };

  const normalizeMediaUrl = (value) => {
    if (!value) return '';

    try {
      const url = new URL(value, window.location.origin);
      url.hash = '';
      return url.toString();
    } catch (error) {
      return String(value).trim();
    }
  };

  const normalizeSrcset = (value) => {
    if (!value) return '';

    return value.split(',')
      .map((candidate) => candidate.trim().replace(/\s+/g, ' '))
      .filter(Boolean)
      .map((candidate) => {
        const parts = candidate.split(' ');
        const url = normalizeMediaUrl(parts.shift());
        return [url].concat(parts).join(' ');
      })
      .join(', ');
  };

  const getCardMediaSignature = (card) => {
    const image = card.querySelector('.product_media img');

    return {
      hasPlaceholder: Boolean(card.querySelector('.product_media .img--placeholder')),
      src: image ? normalizeMediaUrl(image.getAttribute('src')) : '',
      srcset: image ? normalizeSrcset(image.getAttribute('srcset')) : ''
    };
  };

  const mediaSignaturesMatch = (cachedCard, originalCard) => {
    const cached = getCardMediaSignature(cachedCard);
    const original = getCardMediaSignature(originalCard);

    return cached.hasPlaceholder === original.hasPlaceholder &&
      cached.src === original.src &&
      cached.srcset === original.srcset;
  };

  const cachedCardsMatchOriginalMedia = (cachedCards, originalCards) => {
    const originalCardsById = new Map(
      originalCards
        .filter((card) => card.dataset.rqProductId)
        .map((card) => [card.dataset.rqProductId, card])
    );

    return cachedCards.every((cachedCard) => {
      const productId = cachedCard.dataset.rqProductId;
      if (!productId) return false;

      const originalCard = originalCardsById.get(productId);
      if (!originalCard) return false;

      return mediaSignaturesMatch(cachedCard, originalCard);
    });
  };

  const trimStoredPageCache = () => {
    try {
      const index = JSON.parse(sessionStorage.getItem(PAGE_CACHE_INDEX_KEY) || '[]');
      const nextIndex = index.filter((key) => sessionStorage.getItem(`${PAGE_CACHE_PREFIX}${key}`) !== null);

      while (nextIndex.length > MAX_STORED_PAGE_SLICES) {
        const key = nextIndex.shift();
        sessionStorage.removeItem(`${PAGE_CACHE_PREFIX}${key}`);
      }

      sessionStorage.setItem(PAGE_CACHE_INDEX_KEY, JSON.stringify(nextIndex));
    } catch (error) {
      // Storage is opportunistic; failed cleanup should not block rendering.
    }
  };

  const storePageCards = (cacheKey, cards) => {
    try {
      sessionStorage.setItem(
        `${PAGE_CACHE_PREFIX}${cacheKey}`,
        cards.map((card) => card.outerHTML).join('')
      );

      const index = JSON.parse(sessionStorage.getItem(PAGE_CACHE_INDEX_KEY) || '[]')
        .filter((key) => key !== cacheKey);
      index.push(cacheKey);
      sessionStorage.setItem(PAGE_CACHE_INDEX_KEY, JSON.stringify(index));
      trimStoredPageCache();
    } catch (error) {
      try {
        trimStoredPageCache();
      } catch (cleanupError) {
        // Ignore; the cache is a performance enhancement only.
      }
    }
  };

  const clearPreSkeletons = (grid) => {
    getCards(getProductGrid(grid) || grid).forEach((card) => {
      card.classList.remove(PRE_SKELETON_CLASS);
    });
  };

  const shouldSkipPatch = (grid) => {
    const url = new URL(window.location.href);
    const inStockParam = grid.dataset.rqInStockParam;

    if (grid.dataset.rqAvailabilityEnabled !== 'true') return true;
    if (grid.dataset.rqAvailabilityFilterActive === 'true') return true;
    if (grid.dataset.rqAvailabilityManualSort === 'true') return true;
    if (url.searchParams.get(BYPASS_PARAM) === '1') return true;
    if (inStockParam && url.searchParams.has(inStockParam)) return true;

    return false;
  };

  const buildAvailabilitySectionUrl = (grid, paramName, paramValue, page) => {
    const url = new URL(window.location.href);

    url.searchParams.delete(BYPASS_PARAM);
    url.searchParams.delete(grid.dataset.rqInStockParam);
    url.searchParams.delete(grid.dataset.rqOutOfStockParam);
    url.searchParams.set('section_id', grid.dataset.id);
    url.searchParams.set(paramName, paramValue);

    if (page > 1) {
      url.searchParams.set('page', String(page));
    } else {
      url.searchParams.delete('page');
    }

    return url.toString();
  };

  const buildCollectionSectionUrl = (grid, page) => {
    const url = new URL(window.location.href);

    url.searchParams.delete(BYPASS_PARAM);
    url.searchParams.delete(grid.dataset.rqInStockParam);
    url.searchParams.delete(grid.dataset.rqOutOfStockParam);
    url.searchParams.set('section_id', grid.dataset.id);

    if (page > 1) {
      url.searchParams.set('page', String(page));
    } else {
      url.searchParams.delete('page');
    }

    return url.toString();
  };

  const makeSkeleton = () => {
    const card = document.createElement('article');
    card.className = 'product-column rq-collection-patch-skeleton';
    card.setAttribute('aria-hidden', 'true');
    card.innerHTML = `
      <div class="rq-collection-patch-skeleton__media"></div>
      <div class="rq-collection-patch-skeleton__body">
        <span class="rq-collection-patch-skeleton__line rq-collection-patch-skeleton__line--title"></span>
        <span class="rq-collection-patch-skeleton__line rq-collection-patch-skeleton__line--meta"></span>
      </div>
    `;

    return card;
  };

  const insertBeforeControls = (productGrid, element) => {
    productGrid.insertBefore(element, getGridControls(productGrid) || null);
  };

  const fadeOutAndReplace = (skeleton, card) => {
    skeleton.replaceWith(card);
  };

  const fadeOutAndRemove = (skeleton) => {
    skeleton.remove();
  };

  const updateOutOfStockFilterCount = (grid, count) => {
    const selector = `input[name="${grid.dataset.rqOutOfStockParam}"][value="${grid.dataset.rqOutOfStockValue}"]`;

    document.querySelectorAll(selector).forEach((input) => {
      if (!input.checked) input.disabled = count === 0;

      const label = input.nextElementSibling && input.nextElementSibling.matches('label')
        ? input.nextElementSibling
        : null;
      if (!label) return;

      label.childNodes.forEach((node) => {
        if (node.nodeType !== Node.TEXT_NODE) return;
        node.textContent = node.textContent.replace(/(Out of stock\s*)\(\d+\)/i, `$1(${count})`);
      });

      const countEl = label.querySelector('.filter-value-count');
      if (countEl) countEl.textContent = `(${count})`;
    });
  };

  const fetchAvailabilityCards = async (grid, paramName, paramValue, start, needed, available, totalCount, onCard) => {
    const pageSize = toNumber(grid.dataset.rqPageSize);
    const cards = [];
    let page = Math.floor(start / pageSize) + 1;
    let offset = start % pageSize;

    while (cards.length < needed) {
      const response = await fetch(buildAvailabilitySectionUrl(grid, paramName, paramValue, page));
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fetchedGrid = doc.querySelector(GRID_SELECTOR);
      const fetchedProductGrid = fetchedGrid && getProductGrid(fetchedGrid);
      const allPageCards = fetchedProductGrid ? getCards(fetchedProductGrid) : [];
      const pageCards = allPageCards.slice(offset).filter((card) => {
        return card.dataset.rqProductAvailable === String(available);
      });

      if (allPageCards.length <= offset) break;

      pageCards.forEach((card) => {
        if (cards.length >= needed) return;

        cards.push(card);
        if (onCard) onCard(card);
      });

      if (page * pageSize >= totalCount) break;

      page += 1;
      offset = 0;
    }

    return cards;
  };

  const fetchAllMatchingAvailabilityCards = async (grid, paramName, paramValue, available, totalCount, onCard) => {
    const pageSize = toNumber(grid.dataset.rqPageSize);
    const cards = [];
    let page = 1;

    while (pageSize > 0 && (page - 1) * pageSize < totalCount) {
      const response = await fetch(buildAvailabilitySectionUrl(grid, paramName, paramValue, page));
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fetchedGrid = doc.querySelector(GRID_SELECTOR);
      const fetchedProductGrid = fetchedGrid && getProductGrid(fetchedGrid);
      const allPageCards = fetchedProductGrid ? getCards(fetchedProductGrid) : [];

      if (allPageCards.length === 0) break;

      allPageCards.forEach((card) => {
        if (card.dataset.rqProductAvailable !== String(available)) return;

        cards.push(card);
        if (onCard) onCard(card, cards.length - 1);
      });

      if (page * pageSize >= totalCount) break;
      page += 1;
    }

    return cards;
  };

  const getRealOutOfStockCards = async (grid, onCard) => {
    const rawOutOfStockCount = toNumber(grid.dataset.rqOutOfStockCount);
    const cacheKey = [
      window.location.pathname,
      buildNormalizedSearch(1),
      grid.dataset.id,
      grid.dataset.rqOutOfStockParam,
      grid.dataset.rqOutOfStockValue,
      rawOutOfStockCount
    ].join('|');

    if (!realOutOfStockCardsCache.has(cacheKey)) {
      realOutOfStockCardsCache.set(
        cacheKey,
        fetchAllMatchingAvailabilityCards(
          grid,
          grid.dataset.rqOutOfStockParam,
          grid.dataset.rqOutOfStockValue,
          false,
          rawOutOfStockCount,
          onCard
        )
      );
    } else if (onCard) {
      realOutOfStockCardsCache.get(cacheKey).then((cards) => {
        cards.forEach((card, index) => {
          onCard(card, index);
        });
      });
    }

    return realOutOfStockCardsCache.get(cacheKey);
  };

  const getAllCollectionCards = async (grid, onCard) => {
    const pageSize = toNumber(grid.dataset.rqPageSize);
    const visiblePages = getPaginationPageNumbers(grid);
    const fallbackTotalPages = getCurrentPage();
    const totalPages = visiblePages.length > 0 ? Math.max(...visiblePages) : fallbackTotalPages;
    const cacheKey = [
      window.location.pathname,
      buildNormalizedSearch(1),
      grid.dataset.id,
      pageSize,
      totalPages,
      grid.dataset.rqBundleOnly
    ].join('|');

    if (!collectionCardsCache.has(cacheKey)) {
      collectionCardsCache.set(cacheKey, (async () => {
        const cards = [];

        for (let page = 1; page <= totalPages; page += 1) {
          const response = await fetch(buildCollectionSectionUrl(grid, page));
          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fetchedGrid = doc.querySelector(GRID_SELECTOR);
          const fetchedProductGrid = fetchedGrid && getProductGrid(fetchedGrid);
          const pageCards = fetchedProductGrid ? getCards(fetchedProductGrid) : [];

          if (pageCards.length === 0) break;

          pageCards.forEach((card) => {
            cards.push(card);
            if (onCard) onCard(card, cards.length - 1);
          });
        }

        return cards;
      })());
    } else if (onCard) {
      collectionCardsCache.get(cacheKey).then((cards) => {
        cards.forEach((card, index) => {
          onCard(card, index);
        });
      });
    }

    return collectionCardsCache.get(cacheKey);
  };

  const getBundleTargetSlice = async (grid, page = getCurrentPage(), onCards) => {
    const pageSize = toNumber(grid.dataset.rqPageSize);
    const targetStart = (page - 1) * pageSize;
    const cards = [];
    const inStockCards = [];
    const outOfStockCards = [];
    let streamedCount = 0;

    const streamAvailableCards = () => {
      while (streamedCount < pageSize && targetStart + streamedCount < inStockCards.length) {
        cards.push(inStockCards[targetStart + streamedCount]);
        streamedCount += 1;
        if (onCards) onCards(cards.slice());
      }
    };

    const allCards = await getAllCollectionCards(grid, (card) => {
      if (card.dataset.rqProductAvailable === 'true') {
        inStockCards.push(card);
        streamAvailableCards();
        return;
      }

      outOfStockCards.push(card);
    });

    if (allCards.length === 0) return cards;

    if (cards.length < pageSize) {
      const targetCards = inStockCards.concat(outOfStockCards)
        .slice(targetStart, targetStart + pageSize);

      targetCards.slice(cards.length).forEach((card) => {
        cards.push(card);
        if (onCards) onCards(cards.slice());
      });
    }

    return cards;
  };

  const getTargetSlice = async (grid, page = getCurrentPage(), onCards) => {
    if (grid.dataset.rqBundleOnly === 'true') {
      return getBundleTargetSlice(grid, page, onCards);
    }

    const pageSize = toNumber(grid.dataset.rqPageSize);
    const inStockCount = toNumber(grid.dataset.rqInStockCount);
    const rawOutOfStockCount = toNumber(grid.dataset.rqOutOfStockCount);
    const estimatedTotalCount = inStockCount + rawOutOfStockCount;
    const targetStart = (page - 1) * pageSize;
    const estimatedTargetCount = Math.min(pageSize, Math.max(0, estimatedTotalCount - targetStart));
    const cards = [];
    const pushCards = (nextCards) => {
      nextCards.forEach((card) => {
        cards.push(card);
        if (onCards) onCards(cards.slice());
      });
    };

    if (estimatedTargetCount === 0) return cards;

    if (targetStart < inStockCount) {
      const inStockStart = targetStart;
      const inStockNeeded = Math.min(estimatedTargetCount, inStockCount - inStockStart);
      await fetchAvailabilityCards(
        grid,
        grid.dataset.rqInStockParam,
        grid.dataset.rqInStockValue,
        inStockStart,
        inStockNeeded,
        true,
        inStockCount,
        (card) => {
          pushCards([card]);
        }
      );
    }

    const estimatedOutOfStockNeeded = estimatedTargetCount - cards.length;

    if (estimatedOutOfStockNeeded > 0) {
      const outOfStockStart = Math.max(0, targetStart - inStockCount);
      let pushedOutOfStockCount = 0;
      const pushOutOfStockCard = (card, index) => {
        if (index < outOfStockStart) return;
        if (cards.length >= pageSize) return;

        pushedOutOfStockCount += 1;
        pushCards([card]);
      };
      const outOfStockCards = await getRealOutOfStockCards(grid, pushOutOfStockCard);
      const realTotalCount = inStockCount + outOfStockCards.length;
      const realTargetCount = Math.min(pageSize, Math.max(0, realTotalCount - targetStart));
      const outOfStockNeeded = realTargetCount - cards.length;

      if (outOfStockNeeded > 0) {
        const fallbackStart = outOfStockStart + pushedOutOfStockCount;
        pushCards(outOfStockCards.slice(fallbackStart, fallbackStart + outOfStockNeeded));
      }
    }

    return cards;
  };

  const getCachedTargetSlice = (grid, page = getCurrentPage()) => {
    const cacheKey = buildPageCacheKey(grid, page);
    const storedCards = getStoredPageCards(cacheKey);

    if (storedCards) return Promise.resolve(storedCards);

    if (!targetSliceCache.has(cacheKey)) {
      targetSliceCache.set(cacheKey, getTargetSlice(grid, page).then((cards) => {
        if (cards.length > 0) storePageCards(cacheKey, cards);

        return cards.map((card) => card.cloneNode(true));
      }));
    }

    return targetSliceCache.get(cacheKey).then((cards) => {
      return cards.map((card) => card.cloneNode(true));
    });
  };

  const updateOutOfStockFilterCountAsync = (grid) => {
    if (grid.dataset.rqBundleOnly === 'true') return;

    getRealOutOfStockCards(grid)
      .then((cards) => {
        updateOutOfStockFilterCount(grid, cards.length);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const schedulePrefetch = (callback) => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(callback, { timeout: 2000 });
      return;
    }

    window.setTimeout(callback, 250);
  };

  const prefetchPage = (grid, page) => {
    const cacheKey = buildPageCacheKey(grid, page);

    if (prefetchedPages.has(cacheKey) || getStoredPageCards(cacheKey)) return;

    prefetchedPages.add(cacheKey);
    getCachedTargetSlice(grid, page).catch((error) => {
      prefetchedPages.delete(cacheKey);
      console.error(error);
    });
  };

  const prefetchNearbyPages = (grid) => {
    if (shouldSkipPatch(grid)) return;

    const currentPage = getCurrentPage();
    const totalPages = getTotalPages(grid);
    const pages = [];

    for (let page = currentPage + 1; page <= Math.min(totalPages, currentPage + PREFETCH_PAGES_AHEAD); page += 1) {
      pages.push(page);
    }

    if (currentPage > 1) pages.push(currentPage - 1);
    if (pages.length === 0) return;

    schedulePrefetch(() => {
      pages.forEach((page) => {
        prefetchPage(grid, page);
      });
    });
  };

  const renderTargetCards = (productGrid, originalCards, targetCards) => {
    targetCards.forEach((card, index) => {
      card.classList.remove(PRE_SKELETON_CLASS);
      if (originalCards[index]) {
        fadeOutAndReplace(originalCards[index], card);
        return;
      }

      const skeleton = makeSkeleton();
      insertBeforeControls(productGrid, skeleton);
      fadeOutAndReplace(skeleton, card);
    });

    originalCards.slice(targetCards.length).forEach((card) => {
      fadeOutAndRemove(card);
    });
  };

  const createRowStreamer = (productGrid, originalCards, rowSize, targetCount) => {
    let renderedCount = 0;
    let streamTimer = null;
    let latestCards = [];

    const renderCount = (count) => {
      if (count <= renderedCount) return;

      for (let index = renderedCount; index < count; index += 1) {
        const card = latestCards[index];
        if (!card) break;

        card.classList.remove(PRE_SKELETON_CLASS);

        if (originalCards[index]) {
          fadeOutAndReplace(originalCards[index], card);
        } else {
          const skeleton = makeSkeleton();
          insertBeforeControls(productGrid, skeleton);
          fadeOutAndReplace(skeleton, card);
        }
      }

      renderedCount = count;
    };

    const flushRows = (force = false) => {
      if (streamTimer) {
        window.clearTimeout(streamTimer);
        streamTimer = null;
      }

      const completeRowsCount = force
        ? latestCards.length
        : Math.floor(latestCards.length / rowSize) * rowSize;
      const count = Math.min(completeRowsCount, targetCount);

      renderCount(count);

      if (force) {
        originalCards.slice(renderedCount).forEach((card) => {
          fadeOutAndRemove(card);
        });
      }
    };

    return {
      update(cards) {
        latestCards = cards.slice();
        if (latestCards.length - renderedCount < rowSize) return;
        if (streamTimer) return;

        streamTimer = window.setTimeout(() => {
          flushRows(false);
        }, STREAM_ROW_DELAY);
      },
      complete(cards) {
        latestCards = cards.slice();
        flushRows(true);
      }
    };
  };

  const restoreOriginalCards = (productGrid, originalCards) => {
    Array.from(productGrid.querySelectorAll(SKELETON_SELECTOR)).forEach((skeleton) => {
      skeleton.remove();
    });

    originalCards.forEach((card) => {
      card.classList.remove(PRE_SKELETON_CLASS);
      insertBeforeControls(productGrid, card);
    });
  };

  const patchGrid = async (grid) => {
    if (shouldSkipPatch(grid)) {
      clearPreSkeletons(grid);
      return;
    }
    if (grid.dataset.rqAvailabilityPatchLoading === 'true') return;

    const productGrid = getProductGrid(grid);
    if (!productGrid) return;

    const originalCards = getCards(productGrid);
    const hasOutOfStockCard = originalCards.some((card) => card.dataset.rqProductAvailable !== 'true');
    const shouldPatchPage = getCurrentPage() > 1 || hasOutOfStockCard;

    updateOutOfStockFilterCount(grid, toNumber(grid.dataset.rqOutOfStockCount));

    if (!shouldPatchPage) {
      clearPreSkeletons(grid);
      updateOutOfStockFilterCountAsync(grid);
      return;
    }

    const currentPageCacheKey = buildPageCacheKey(grid, getCurrentPage());
    const cachedTargetCards = getStoredPageCards(currentPageCacheKey);

    if (cachedTargetCards) {
      if (cachedCardsMatchOriginalMedia(cachedTargetCards, originalCards)) {
        renderTargetCards(productGrid, originalCards, cachedTargetCards);
        grid.dataset.rqAvailabilityPatched = 'true';
        updateOutOfStockFilterCountAsync(grid);
        return;
      }

      removeStoredPageCards(currentPageCacheKey);
    }

    originalCards.forEach((card) => {
      card.classList.add(PRE_SKELETON_CLASS);
    });

    grid.dataset.rqAvailabilityPatchLoading = 'true';

    try {
      const pageSize = toNumber(grid.dataset.rqPageSize);
      const targetCount = Math.min(pageSize, originalCards.length);
      const streamer = createRowStreamer(productGrid, originalCards, getRowSize(productGrid), targetCount);
      const cacheKey = buildPageCacheKey(grid, getCurrentPage());
      const targetCards = targetSliceCache.has(cacheKey)
        ? await getCachedTargetSlice(grid)
        : await getTargetSlice(grid, getCurrentPage(), (cards) => {
          streamer.update(cards);
        });

      if (targetCards.length === 0) {
        clearPreSkeletons(grid);
        return;
      }

      storePageCards(cacheKey, targetCards);
      streamer.complete(targetCards);
      grid.dataset.rqAvailabilityPatched = 'true';
    } catch (error) {
      console.error(error);
      restoreOriginalCards(productGrid, originalCards);
    } finally {
      grid.dataset.rqAvailabilityPatchLoading = 'false';
    }
  };

  const init = () => {
    document.querySelectorAll(GRID_SELECTOR).forEach((grid) => {
      patchGrid(grid);
      prefetchNearbyPages(grid);
    });
  };

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('rq:collection-grid-updated', init);
}());
